import { Component, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { DraftResponse } from './import.models';

@Component({
  selector: 'app-import-validate',
  imports: [RouterLink],
  template: `
    <section class="card">
      <header class="head">
        <div>
          <h1>Dry Run Results</h1>
          <p class="hint">Review validation results before importing reservations.</p>
        </div>
        <div class="actions">
          <button type="button" class="secondary" (click)="rerun()" [disabled]="busy()">{{ busy() ? 'Running…' : 'Run Dry Run Again' }}</button>
          <a routerLink="/import/map" class="button secondary">Back to Mapping</a>
          <button type="button" class="secondary" (click)="abort()">Abort</button>
          <button type="button" (click)="goToImport()" [disabled]="busy() || !canImport()">Import valid rows</button>
          @if ((draft()?.validation?.errors || []).length > 0) {
            <button type="button" class="secondary" (click)="downloadErrors()">Download Error CSV</button>
          }
        </div>
      </header>

      @if (message()) {
        <div class="msg" [class.err]="isError()">{{ message() }}</div>
      }

      @if (draft()?.validation) {
        <section class="results">
          <div class="stats">
            <div><strong>{{ draft()?.fileName || 'uploaded.csv' }}</strong><span>File</span></div>
            <div><strong>{{ draft()?.validation?.rowsRead }}</strong><span>Rows read</span></div>
            <div><strong>{{ draft()?.validation?.validRows }}</strong><span>Valid rows</span></div>
            <div><strong>{{ draft()?.validation?.invalidRows }}</strong><span>Invalid rows</span></div>
            <div><strong>{{ draft()?.validation?.warningCount }}</strong><span>Warning rows</span></div>
          </div>

          @if ((draft()?.validation?.blockedReasons || []).length > 0) {
            <div class="panel panel-error">
              @for (reason of draft()?.validation?.blockedReasons || []; track reason) {
                <div>{{ reason }}</div>
              }
            </div>
          }

          @if (draft()?.validation?.seasonCoverage) {
            <div class="panel" [class.panel-error]="draft()?.validation?.seasonCoverage?.blocked">
              {{ draft()?.validation?.seasonCoverage?.blocked ? draft()?.validation?.seasonCoverage?.message : draft()?.validation?.seasonCoverage?.note }}
            </div>
          }

          @if ((draft()?.validation?.roomValidation?.notes || []).length > 0) {
            <div class="panel">
              @for (note of draft()?.validation?.roomValidation?.notes || []; track note) {
                <div>{{ note }}</div>
              }
            </div>
          }

          @if ((draft()?.validation?.errors || []).length > 0) {
            <table>
              <thead><tr><th>Row</th><th>CRSResID</th><th>Errors</th></tr></thead>
              <tbody>
                @for (row of draft()?.validation?.errors || []; track row.row) {
                  <tr><td>{{ row.row }}</td><td>{{ row.crsResId }}</td><td>{{ row.errors.join('; ') }}</td></tr>
                }
              </tbody>
            </table>
          }

          @if ((draft()?.validation?.warnings || []).length > 0) {
            <table>
              <thead><tr><th>Row</th><th>CRSResID</th><th>Warnings</th></tr></thead>
              <tbody>
                @for (row of draft()?.validation?.warnings || []; track row.row) {
                  <tr><td>{{ row.row }}</td><td>{{ row.crsResId }}</td><td>{{ row.warnings.join('; ') }}</td></tr>
                }
              </tbody>
            </table>
          }
        </section>
      }
    </section>
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:18px;display:grid;gap:14px}
    .head{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;flex-wrap:wrap}
    h1{margin:0}
    .hint{margin:4px 0 0;color:var(--muted)}
    .actions{display:flex;gap:10px;flex-wrap:wrap}
    .button,button{height:40px;display:inline-flex;align-items:center;justify-content:center;border:0;border-radius:10px;background:var(--primary);color:#fff;font-weight:700;padding:0 14px;cursor:pointer;text-decoration:none}
    .secondary{background:#6b778c}
    .msg{padding:10px 12px;border-radius:10px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5}
    .msg.err{background:#fdecec;color:#8a1f1f;border-color:#f2c4c4}
    .results{display:grid;gap:12px}
    .stats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px}
    .stats div{padding:12px;border:1px solid #e2e8f4;border-radius:12px;background:#f8fbff;display:grid}
    .stats strong{font-size:22px}
    .stats span{color:var(--muted);font-size:12px;text-transform:uppercase;letter-spacing:.04em}
    .panel{padding:10px;border-radius:10px;background:#eef7ef;border:1px solid #cadfc9;color:#1d5d25}
    .panel-error{background:#fdecec;border-color:#f4c0c0;color:#a42323}
    table{width:100%;border-collapse:collapse}
    th,td{padding:8px;border-bottom:1px solid #edf0f6;text-align:left;vertical-align:top}
    @media (max-width: 900px){ .stats{grid-template-columns:1fr} }
  `,
})
export class ImportValidateComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly busy = signal(false);
  readonly message = signal('');
  readonly isError = signal(false);
  readonly draft = signal<DraftResponse | null>(null);

  async ngOnInit(): Promise<void> {
    await this.rerun();
  }

  async rerun(): Promise<void> {
    this.busy.set(true);
    this.message.set('');
    this.isError.set(false);
    try {
      const res = await firstValueFrom(this.api.post<{ ok: boolean; error?: string; draft?: DraftResponse }>('/import/validate', {}));
      if (!res.ok) {
        this.isError.set(true);
        this.message.set(res.error || 'Dry run failed.');
      }
      const draft = await firstValueFrom(this.api.get<DraftResponse>('/import/draft'));
      if (!draft.hasDraft) {
        await this.router.navigateByUrl('/import/upload');
        return;
      }
      this.draft.set(draft);
      if (res.ok) {
        this.message.set('Dry run complete.');
      }
    } catch {
      this.isError.set(true);
      this.message.set('Dry run failed.');
    } finally {
      this.busy.set(false);
    }
  }

  canImport(): boolean {
    const validation = this.draft()?.validation;
    return Boolean(validation && validation.validRows > 0 && validation.blockedReasons.length === 0);
  }

  async abort(): Promise<void> {
    await firstValueFrom(this.api.post('/import/reset', {}));
    await this.router.navigateByUrl('/import/upload');
  }

  async goToImport(): Promise<void> {
    await this.router.navigateByUrl('/import/commit');
  }

  downloadErrors(): void {
    window.open('/api/import/error-csv', '_blank');
  }
}
